import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { routes } from "../../../shared/config/routes";
import { ScheduleConflictDialogs } from "../../../shared/ui/feedback/ScheduleConflictDialogs/ScheduleConflictDialogs";
import { useSchedule, useScheduleFreshness } from "../../../entities/schedule";
import {
  CalendarOrderLeaveGuard,
  useCalendarOrderEditor,
} from "../../../features/technician-calendar-order";
import { useUpsertAppointment } from "../../../features/appointment-upsert";
import { useUpsertTask } from "../../../features/task-upsert";
import {
  PlanningBar,
  useCommitPlan,
  useDispositionMode,
  usePlanningDraft,
  useRescheduleSource,
} from "../../../features/disposition-planning";
import {
  DispositionBoardWidget,
  DispositionToolbar,
  DispositionWeekWidget,
  useDispositionDay,
  useDispositionView,
  useDispositionWeek,
  usePlanningGewerkId,
  type PlanningDraft,
} from "../../../widgets/disposition-board";

/** What the day board last said about the draft it is drawing — see `DispositionBoardWidget`. */
interface BoardDraft {
  draft: PlanningDraft;
  technicianName: string | null;
}

export const DispositionPage = () => {
  // Owned here so the navigation can sit in the page header while the widget renders the grid.
  // Both day and week read the same `?day=` anchor, so mounting the two hooks side by side is
  // cheap — only the widget that is on screen fetches anything.
  const view = useDispositionView();
  const day = useDispositionDay();
  const week = useDispositionWeek();
  const navigate = useNavigate();

  const isWeek = view.value === "woche";
  const range = isWeek ? week : day;

  /**
   * The planning state lives here, not in the day board, because it has to outlive it: the dispatcher
   * looks at Woche to pick a day, and the day board — with everything it owned — is unmounted for as
   * long as they look. The bar, the draft and the save are what stay.
   */
  const plan = useDispositionMode();
  const source = useRescheduleSource(plan);
  const planning = usePlanningDraft(day.dayIso, plan.mode, source?.seed);

  const appointmentModal = useUpsertAppointment();
  const taskModal = useUpsertTask();
  const {
    openCreate: openCreateAppointment,
    openEdit: openEditAppointment,
    close: closeAppointmentModal,
  } = appointmentModal;
  const { openCreate: openCreateTask, openEdit: openEditTask, close: closeTaskModal } = taskModal;

  /**
   * A form is docked on the day board — the board owns it, and the board is what Woche would
   * unmount, taking the typed input with it. So while a form is open the board stays mounted behind
   * the week, hidden, and the form stays docked beside it.
   */
  const formOpen = appointmentModal.isOpen || taskModal.isOpen;

  /**
   * The Gewerk of the Auftrag being planned or edited, for ordering the Handwerker on both boards.
   * Resolved here, once, so day and week cannot disagree.
   */
  const matchGewerkId = usePlanningGewerkId({
    planMode: plan.mode,
    planWorkOrderId: plan.workOrderId,
    sourceWorkOrderId: source?.workOrderId ?? null,
    appointmentForm: {
      isOpen: appointmentModal.isOpen,
      mode: appointmentModal.mode,
      appointmentId: appointmentModal.appointmentId,
      workOrderId: appointmentModal.workOrderId,
    },
    taskFormOpen: taskModal.isOpen,
  });
  const keepBoardMounted = !isWeek || formOpen;

  /**
   * The Leitung's edit mode for the calendar order. Owned here so the draft survives Tag ↔ Woche,
   * and ended the moment planning starts: Planen and Umplanen have their own order and the rows
   * under the editor would change from beneath it.
   */
  const orderEditor = useCalendarOrderEditor();
  const planningRoster = plan.isPlanning || formOpen;
  const { isEditing: isEditingOrder, cancel: cancelOrderEdit } = orderEditor;
  useEffect(() => {
    if (planningRoster && isEditingOrder) cancelOrderEdit();
  }, [planningRoster, isEditingOrder, cancelOrderEdit]);

  /**
   * Only the day board has the rows to say whether the draft collides or leaves the shift, and what
   * the form's live slot is. It reports both while it is mounted and `null` when it goes; in Woche
   * the bar therefore falls back to the picked draft with no advisories — saving is still checked by
   * the API, whose dialogs are raised below.
   */
  const [boardDraft, setBoardDraft] = useState<BoardDraft | null>(null);
  const handleDraftChange = useCallback(
    (draft: PlanningDraft | null, technicianName: string | null) =>
      setBoardDraft(draft ? { draft, technicianName } : null),
    [],
  );

  /**
   * Planning is done — drop out of the mode so the board goes back to normal.
   *
   * Guarded: the modals also fire `onSuccess` for an ordinary edit opened by clicking a block, and
   * that must not clear URL params it never set.
   */
  const handlePlanSaved = useCallback(() => {
    if (!plan.isPlanning) return;
    planning.clear();
    plan.exit();
  }, [plan, planning]);

  // Only a brand-new Aufgabe needs it; cleared with the rest of the draft when planning ends.
  const [taskTitle, setTaskTitle] = useState("");

  const commitPlan = useCommitPlan({
    plan,
    source,
    // What the board draws, not `planning.draft`: with a form docked the form's slot is the one on
    // screen (and the one a drag edits), so saving the older picked slot would book another time.
    draft: boardDraft?.draft.state ?? planning.draft,
    dayIso: day.dayIso,
    title: taskTitle,
    onSaved: () => {
      setTaskTitle("");
      // Whatever the dispatcher clicked, the panel's job is done: its fields were either what got
      // saved or are now stale. Closing is a URL write, so it also drops the `upsert-*` params.
      closeAppointmentModal();
      closeTaskModal();
      handlePlanSaved();
    },
  });

  const handleOpenForm = useCallback(() => {
    // No draft needed: the form opens on its defaults, and a later click on a technician's row
    // writes the slot into it (board → form sync in `UpsertAppointmentModal`).
    if (plan.mode === "schedule-appointment") {
      // An existing Termin is moved through the *edit* form, so the picked slot lands on the
      // appointment that is already there instead of creating a second one.
      if (plan.appointmentId != null) openEditAppointment(plan.appointmentId);
      else openCreateAppointment(plan.workOrderId ?? undefined);
      return;
    }
    // An existing task gets its first Termin through the *edit* form — `PATCH /tasks/:id` ignores
    // schedule fields on a task with no entry, so the modal follows it with `/tasks/:id/schedule`.
    if (plan.taskId != null) openEditTask(plan.taskId);
    else openCreateTask();
  }, [
    plan.mode,
    plan.workOrderId,
    plan.appointmentId,
    plan.taskId,
    openCreateAppointment,
    openEditAppointment,
    openCreateTask,
    openEditTask,
  ]);

  /**
   * The freshness of the board on screen, read without moving anyone's fetch.
   *
   * `useDispositionBoard` and `useDispositionWeekBoard` both call
   * `useSchedule({ from, to, includeWorkOrder: true })` internally but narrow their return to
   * `{ rows, isLoading, isError, refetch }`, so the timestamps never reach this layer. Rather than
   * widening both hooks or lifting the fetch out of the widgets, this subscribes to the **same
   * query key** — one extra observer on a cache entry that already exists, and no extra request.
   *
   * That only holds while the arguments match exactly. If a widget ever changes its range or drops
   * `includeWorkOrder`, this becomes a second cache entry and a second request rather than a
   * compile error, so the three call sites have to move together.
   */
  const scheduleQuery = useSchedule({
    from: range.from,
    to: range.to,
    includeWorkOrder: true,
  });
  const { lastUpdatedAt, isFetching, isDisconnected } = useScheduleFreshness([scheduleQuery]);

  const draftOnBoard = isWeek ? null : boardDraft;

  return (
    <PageLayout
      pageTitle="Kalender"
      scrollable={false}
      headerChildren={
        <DispositionToolbar
          view={view}
          day={day}
          week={week}
          lastUpdatedAt={lastUpdatedAt}
          isFetching={isFetching}
          isDisconnected={isDisconnected}
          onRefresh={() => {
            void scheduleQuery.refetch();
          }}
        />
      }
    >
      <div className="flex h-full flex-col">
        <div className="flex min-h-0 flex-1 flex-col">
          {isWeek && (
            <DispositionWeekWidget
              week={week}
              matchGewerkId={matchGewerkId}
              isPlanning={planningRoster}
              orderEditor={orderEditor}
            />
          )}
          {keepBoardMounted && (
            <div className={isWeek ? "contents" : "flex min-h-0 flex-1 flex-col"}>
              <DispositionBoardWidget
                day={day}
                plan={plan}
                source={source}
                planning={planning}
                onPlanSaved={handlePlanSaved}
                onDraftChange={handleDraftChange}
                active={!isWeek}
                matchGewerkId={matchGewerkId}
                orderEditor={orderEditor}
              />
            </div>
          )}
        </div>

        {plan.isPlanning && (
          <PlanningBar
            plan={plan}
            dayIso={day.dayIso}
            source={source}
            onGoToSourceDay={source ? () => day.goToDay(source.dayIso) : undefined}
            // In Woche nothing can be placed, and a save would book the anchor day the week happens
            // to hang on — so the bar shows no draft there and offers neither button.
            draft={isWeek ? null : planning.draft}
            pickedTechnicianName={draftOnBoard?.technicianName ?? null}
            colliding={draftOnBoard?.draft.colliding ?? false}
            outsideShift={draftOnBoard?.draft.outsideShift ?? false}
            onSave={commitPlan.commit}
            onOpenForm={handleOpenForm}
            canSave={!isWeek && commitPlan.canCommit}
            isSaving={commitPlan.isPending}
            taskTitle={taskTitle}
            onTaskTitleChange={setTaskTitle}
            isWeekView={isWeek}
          />
        )}
      </div>

      <CalendarOrderLeaveGuard editor={orderEditor} />

      {/* After the day board's own dialogs in the DOM: every modal shares z-index 100, and this one
          answers the bar's save, which can happen while a docked form is open. */}
      <ScheduleConflictDialogs
        flow={commitPlan.conflictFlow}
        isLoading={commitPlan.isPending}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
        conflictTitle="Terminüberschneidung erkannt"
        conflictDescription={`An dieser Zeit liegt bereits ein Eintrag. Möchten Sie trotzdem ${source ? "verschieben" : "planen"}?`}
        conflictConfirmLabel={source ? "Trotzdem verschieben" : "Trotzdem planen"}
        workingHoursDescription={`Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie trotzdem ${source ? "verschieben" : "planen"}?`}
        blockersDescription="Die folgenden abgeschlossenen Einträge blockieren diesen Zeitraum."
      />
    </PageLayout>
  );
};
